import { CustomerState } from './../../state/customer/customer.reducer';
import { Component, OnInit, Output } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { selectCurrentSalesUser } from '../../state/sales-user/sales-user.selector';
import {
  getCustomerByReflexId,
  getGatewayCustomerByReflexId,
  updateCoins,
  clearSelectedCustomer,
  clearSelectedGatewayCustomer,
} from '../../state/customer/customer.actions';
import {
  selectCustomer,
  selectSelectedCustomer,
  selectSelectedGatewayCustomer,
} from '../../state/customer/customer.selector';
import { GatewayCustomer } from '../../models/GatewayCustomer';
import { Observable } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { Location } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Customer } from '../../models/Customer';
import {
  getOrdersByMe,
  changeOrderStatus,
  getOrdersByCustomerId,
} from '../../state/order/order.actions';
import { selectGetOrders } from '../../state/order/order.selector';
import { Order } from '../../models/Order';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { AddCoinsDialogComponent } from './add-coins-dialog/add-coins-dialog.component';
import { SendInvitationDialogComponent } from './send-invitation-dialog/send-invitation-dialog.component';
import { FormsModule } from '@angular/forms';
import { EventEmitter } from '@angular/core';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { loadSalesUserMe } from '../../state/sales-user/sales-user.actions';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import CustomerService from '../../services/CustomerService';
import { PageHeaderComponent, HeaderAction } from '../page-header/page-header.component';

@Component({
  selector: 'app-customer-details',
  standalone: true,
  templateUrl: './customer-details.component.html',
  styleUrl: './customer-details.component.css',
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatDialogModule,
    FormsModule,
    MatChipsModule,
    MatDividerModule,
    MatSnackBarModule,
    RouterLink,
    PageHeaderComponent
  ],
})
export class CustomerDetailsComponent implements OnInit {
  gatewayCustomer$: Observable<GatewayCustomer | null>;
  customer$: Observable<Customer | null>;
  orders$: Observable<Order[]>;
  id: string = '';
  @Output() confirm = new EventEmitter<void>();
  headerActions: HeaderAction[] = [];

  salesUserCurrentCoins: number | null = null;

  constructor(
    private route: ActivatedRoute,
    private store: Store<{ customer: CustomerState }>,
    private location: Location,
    private dialog: MatDialog,
    private customerService: CustomerService,
    private snackBar: MatSnackBar
  ) {
    this.gatewayCustomer$ = this.store.select(selectSelectedGatewayCustomer);
    this.customer$ = this.store.select(selectSelectedCustomer);
    
    // Initialize header actions
    this.headerActions = [
      {
        label: 'Einladung senden',
        icon: 'email',
        onClick: () => this.openSendInvitationDialog(),
        variant: 'primary'
      }
    ];
    this.orders$ = this.store.select(selectGetOrders);
    this.store.select(selectCurrentSalesUser).subscribe(su => {
      this.salesUserCurrentCoins = su?.currentCoins ?? null;
    });
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.id = params.get('id') ?? '';
      console.log('CustomerDetailsComponent initialized with id:', this.id);
      if (this.id) {
        this.store.dispatch(clearSelectedCustomer());
        this.store.dispatch(clearSelectedGatewayCustomer());
        this.store.dispatch(getGatewayCustomerByReflexId({ id: this.id }));
        this.store.dispatch(getCustomerByReflexId({ id: this.id }));
        this.store.dispatch(
          getOrdersByCustomerId({ customerId: Number(this.id) })
        );
        this.store.dispatch(loadSalesUserMe());
      }
    });
  }

  back() {
    this.location.back();
  }

  changeOrderStatus(
    orderId: number | undefined,
    action: 'approve' | 'decline'
  ) {
    let coinsUsed = 0;
    this.orders$.subscribe((orders) => {
      const order = orders.find((o) => o.id === orderId);
      coinsUsed = order!.coinsUsed || 0;
    });
    if (typeof orderId === 'number') {
      this.store.dispatch(changeOrderStatus({ orderId, action }));
      if (action === 'decline') {
        this.store.dispatch(
          updateCoins({
            request: {
              userName: localStorage.getItem('userName') || '',
              coins: coinsUsed,
              operation: 'add-refund',
            },
          })
        );
      }
    }
  }

  openAddCoinsDialog(reflexCustomerNo?: number) {
    console.log(
      'Opening Add Coins Dialog for reflexCustomerNo:',
      reflexCustomerNo
    );
    if (reflexCustomerNo) {
      this.dialog.open(AddCoinsDialogComponent, {
        data: { reflexCustomerNo, salesUserCurrentCoins: this.salesUserCurrentCoins },
      });
    }
  }

  openSendInvitationDialog() {
    console.log('Opening Send Invitation Dialog');

    // Get customer number from the current customer
    let customerNo: number | null = null;
    this.gatewayCustomer$.subscribe(gc => {
      if (gc && gc.customerNo) {
        customerNo = gc.customerNo;
      }
    }).unsubscribe();

    if (!customerNo) {
      console.error('No customer number available');
      return;
    }

    const dialogRef = this.dialog.open(SendInvitationDialogComponent, {
      width: '600px',
      disableClose: false,
      data: { customerNo: customerNo }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log('Invitation sent successfully');
        // Reload customer data to reflect any changes
        if (this.id) {
          this.store.dispatch(getCustomerByReflexId({ id: this.id }));
        }
      }
    });
  }

  approveCustomer(customerId: number | undefined): void {
    if (!customerId) return;

    this.customerService.approveCustomer(customerId).subscribe({
      next: () => {
        this.snackBar.open('Kunde erfolgreich freigeschaltet!', 'OK', { duration: 3000 });
        // Reload customer data
        if (this.id) {
          this.store.dispatch(getCustomerByReflexId({ id: this.id }));
        }
      },
      error: (error) => {
        console.error('Error approving customer:', error);
        this.snackBar.open('Fehler beim Freischalten des Kunden', 'OK', { duration: 3000 });
      }
    });
  }

  rejectCustomer(customerId: number | undefined): void {
    if (!customerId) return;

    if (confirm('Möchten Sie diesen Kunden wirklich ablehnen?')) {
      this.customerService.rejectCustomer(customerId).subscribe({
        next: () => {
          this.snackBar.open('Kunde wurde abgelehnt', 'OK', { duration: 3000 });
          // Reload customer data
          if (this.id) {
            this.store.dispatch(getCustomerByReflexId({ id: this.id }));
          }
        },
        error: (error) => {
          console.error('Error rejecting customer:', error);
          this.snackBar.open('Fehler beim Ablehnen des Kunden', 'OK', { duration: 3000 });
        }
      });
    }
  }

  getStatusColor(status: string): string {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'warn';
      case 'unlocked':
        return 'primary';
      case 'rejected':
        return 'accent';
      default:
        return '';
    }
  }

  getStatusLabel(status: string): string {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'Ausstehend';
      case 'unlocked':
        return 'Freigeschaltet';
      case 'rejected':
        return 'Abgelehnt';
      default:
        return status || 'Unbekannt';
    }
  }
}
